import { ReadonlyURLSearchParams } from 'next/navigation';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';

export type ConsentType = {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
};

const CONSENT_KEY = 'consentMode';

const getParentDomain = (): string | undefined => {
  if (typeof location === 'undefined') return undefined;
  const parts = location.hostname.split('.');
  if (parts.length > 2) return '.' + parts.slice(parts.length - 2).join('.');
  return '.' + location.hostname;
};

const readSharedConsent = (): ConsentType | null => {
  try {
    const cookieValue = getCookie(CONSENT_KEY);
    if (cookieValue) return JSON.parse(cookieValue as string) as ConsentType;
  } catch (e) {}

  if (typeof localStorage !== 'undefined') {
    const local = localStorage.getItem(CONSENT_KEY);
    if (local) {
      try {
        return JSON.parse(local) as ConsentType;
      } catch (e) {}
    }
  }
  return null;
};

const clearSharedConsent = (): void => {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(CONSENT_KEY);

  if (typeof window !== 'undefined' && window.navigator.cookieEnabled) {
    deleteCookie(CONSENT_KEY, { domain: getParentDomain(), path: '/' });
    deleteCookie(CONSENT_KEY, { path: '/' });
  }
};

const openConsentPreferences = (): void => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('open-consent-preferences'));
};

const writeSharedConsent = (consent: ConsentType): void => {
  if (typeof localStorage !== 'undefined') localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

  if (typeof window !== 'undefined' && window.navigator.cookieEnabled) {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);

    setCookie(CONSENT_KEY, JSON.stringify(consent), {
      expires,
      domain: getParentDomain(),
      secure: true,
      sameSite: 'lax',
      path: '/',
    });
  }
};

const EPHEMERAL_ATTRIBUTION_KEY = 'sum_click_ids';
const CLICK_ID_KEYS = ['gclid', 'wbraid', 'gbraid', 'gad_source', 'bp_id'] as const;
const UTM_KEY_PATTERN = /^utm_/i;
const MAX_UTM_PARAMS = 5;

const extractUtmParams = (searchParams: URLSearchParams | ReadonlyURLSearchParams): Record<string, string> => {
  const out: Record<string, string> = {};
  let count = 0;
  searchParams.forEach((value, rawKey) => {
    if (count >= MAX_UTM_PARAMS) return;
    if (!UTM_KEY_PATTERN.test(rawKey)) return;
    const key = rawKey.toLowerCase();
    if (key in out || !value) return;
    out[key] = value;
    count += 1;
  });
  return out;
};

const captureEphemeralAttribution = (searchParams: ReadonlyURLSearchParams): void => {
  try {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
    let existing: Record<string, unknown> = {};
    try {
      const raw = sessionStorage.getItem(EPHEMERAL_ATTRIBUTION_KEY);
      if (raw) existing = JSON.parse(raw) || {};
    } catch (e) {}

    const incoming: Record<string, string> = {
      ...extractUtmParams(searchParams),
    };
    for (const key of CLICK_ID_KEYS) {
      const value = searchParams.get(key);
      if (value) incoming[key] = value;
    }

    let changed = false;
    for (const key of Object.keys(incoming)) {
      if (existing[key] !== incoming[key]) {
        existing[key] = incoming[key];
        changed = true;
      }
    }
    if (incoming.bp_id) {
      existing.bp_id_captured_at = Date.now();
      changed = true;
    }
    if (changed) sessionStorage.setItem(EPHEMERAL_ATTRIBUTION_KEY, JSON.stringify(existing));
  } catch (e) {}
};

/** Read the ephemeral attribution params captured for this tab session, or `{}`. */
const readEphemeralClickIds = (): Record<string, unknown> => {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(EPHEMERAL_ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const UTM_COOKIE_TTL_DAYS = 90;

const BP_ID_TTL_MS = UTM_COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000;

// Also applied by the inline script of src/app/layout.tsx. Undated: a cookie from before the date existed.
const isBpIdFresh = (capturedAt: unknown, now: number = Date.now()): boolean => {
  if (capturedAt === undefined) return true;
  return typeof capturedAt === 'number' && Number.isFinite(capturedAt) && now - capturedAt <= BP_ID_TTL_MS;
};

const readRelayBpId = (
  relay: Record<string, unknown>,
  now: number = Date.now()
): { bp_id: string; capturedAt?: number } | null => {
  const bp_id = relay.bp_id;
  if (typeof bp_id !== 'string' || bp_id === '') return null;
  const capturedAt = relay.bp_id_captured_at;
  if (!isBpIdFresh(capturedAt, now)) return null;
  return typeof capturedAt === 'number' ? { bp_id, capturedAt } : { bp_id };
};

const UTM_COOKIE_KEYS = [
  'gad_source',
  'gclid',
  'wbraid',
  'gbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'bp_id',
] as const;

const pickUtmCookieValues = (read: (key: string) => unknown): UtmData => {
  const out: Record<string, string> = {};
  for (const key of UTM_COOKIE_KEYS) {
    const value = read(key);
    if (typeof value === 'string' && value !== '') out[key] = value;
  }
  return out as UtmData;
};

const CAMPAIGN_COOKIE_KEYS = UTM_COOKIE_KEYS.filter((key) => key !== 'bp_id');

// A campaign (click ids and utm_*) is replaced as a whole, never mixed with the previous one.
export const mergeUtmCookieData = (
  stored: UtmData,
  incoming: UtmData,
  now: number,
  incomingBpIdCapturedAt?: number
): UtmData => {
  const hasCampaign = CAMPAIGN_COOKIE_KEYS.some((key) => typeof incoming[key] === 'string' && incoming[key] !== '');
  const campaignSource = hasCampaign ? incoming : stored;
  const merged: Record<string, string | number> = {};
  for (const key of CAMPAIGN_COOKIE_KEYS) {
    const value = campaignSource[key];
    if (typeof value === 'string' && value !== '') merged[key] = value;
  }

  if (typeof incoming.bp_id === 'string' && incoming.bp_id !== '') {
    merged.bp_id = incoming.bp_id;
    merged.bp_id_captured_at =
      typeof incomingBpIdCapturedAt === 'number' && incomingBpIdCapturedAt <= now ? incomingBpIdCapturedAt : now;
  } else if (typeof stored.bp_id === 'string' && stored.bp_id !== '') {
    merged.bp_id = stored.bp_id;
    merged.bp_id_captured_at = stored.bp_id_captured_at ?? now;
  }
  return merged as UtmData;
};

const writeUtmCookie = (incoming: UtmData, incomingBpIdCapturedAt?: number): void => {
  if (Object.keys(incoming).length === 0) return;

  const consent = readSharedConsent();
  if (
    !window.navigator.cookieEnabled ||
    typeof sessionStorage === 'undefined' ||
    typeof localStorage === 'undefined' ||
    !consent ||
    consent.analytics_storage !== 'granted'
  ) {
    return;
  }

  const now = Date.now();
  const utm_datas = mergeUtmCookieData(getUtmData(), incoming, now, incomingBpIdCapturedAt);

  const expires = new Date(now);
  expires.setDate(expires.getDate() + UTM_COOKIE_TTL_DAYS);

  setCookie('utm_datas', JSON.stringify(utm_datas), {
    expires,
    domain: getParentDomain(),
    secure: true,
    sameSite: 'none',
  });
};

const utmManager = (searchParams: ReadonlyURLSearchParams): void => {
  captureEphemeralAttribution(searchParams);
  writeUtmCookie(pickUtmCookieValues((key) => searchParams.get(key)));
};

// The banner often shows after an internal navigation: the ids are then only in the session relay.
const utmManagerOnConsent = (searchParams: ReadonlyURLSearchParams): void => {
  captureEphemeralAttribution(searchParams);
  const relay = readEphemeralClickIds();
  const fromRelay = pickUtmCookieValues((key) => relay[key]);
  const fromUrl = pickUtmCookieValues((key) => searchParams.get(key));
  const hasCampaignInUrl = Object.keys(fromUrl).some((key) => key !== 'bp_id');
  const relayBpId = readRelayBpId(relay);

  const merged: UtmData = hasCampaignInUrl ? { ...fromUrl } : { ...fromRelay, ...fromUrl };
  delete merged.bp_id;
  let bpIdCapturedAt: number | undefined;
  if (fromUrl.bp_id) {
    merged.bp_id = fromUrl.bp_id;
  } else if (relayBpId) {
    merged.bp_id = relayBpId.bp_id;
    bpIdCapturedAt = relayBpId.capturedAt;
  }
  writeUtmCookie(merged, bpIdCapturedAt);
};

/** Marketing attribution stored in the `utm_datas` cookie. Every field is optional.
 *  `bp_id` is the referring Business Partner (affiliate) id. */
export type UtmData = {
  gad_source?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  bp_id?: string;
  bp_id_captured_at?: number;
};

/** Read + parse the `utm_datas` cookie. Returns an empty object when the cookie
 *  is absent (no attribution / consent not granted) or malformed — callers must
 *  treat every field as possibly undefined. A `bp_id` past its 90 days is left out. */
const getUtmData = (): UtmData => {
  try {
    const raw = getCookie('utm_datas');
    if (typeof raw !== 'string' || raw.length === 0) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const data = { ...parsed } as UtmData;
    if (!data.bp_id || !isBpIdFresh(data.bp_id_captured_at)) {
      delete data.bp_id;
      delete data.bp_id_captured_at;
    }
    return data;
  } catch {
    return {};
  }
};

const BP_TS_PARAM = 'bp_ts';

const pickBpIdForDecoration = (
  urlParams: URLSearchParams | null,
  cookie: UtmData,
  relay: Record<string, unknown>,
  now: number = Date.now()
): { bp_id: string; bp_ts?: number } | null => {
  const fromUrl = urlParams?.get('bp_id');
  if (fromUrl) return { bp_id: fromUrl, bp_ts: now };
  if (typeof cookie.bp_id === 'string' && cookie.bp_id !== '') {
    const at = cookie.bp_id_captured_at;
    return typeof at === 'number' ? { bp_id: cookie.bp_id, bp_ts: at } : { bp_id: cookie.bp_id };
  }
  const fromRelay = readRelayBpId(relay, now);
  if (fromRelay) return { bp_id: fromRelay.bp_id, bp_ts: fromRelay.capturedAt };
  return null;
};

/**
 * Named click / affiliate ids forwarded to the dashboard in the `/register` URL.
 * These don't start with `utm_`, so they're listed explicitly; the `utm_*` params
 * are discovered dynamically in `getClickIdsForDecoration` (see CLICK_ID_KEYS /
 * extractUtmParams). `fbclid` is intentionally NOT forwarded here — it stays
 * consent-gated in the `utm_datas` cookie (Meta targeting id).
 */
const getClickIdsForDecoration = (): Record<string, string> => {
  const out: Record<string, string> = {};
  let urlParams: URLSearchParams | null = null;
  try {
    if (typeof window !== 'undefined') urlParams = new URLSearchParams(window.location.search);
  } catch {
    urlParams = null;
  }
  const cookie = getUtmData();
  const ephemeral = readEphemeralClickIds();

  for (const key of CLICK_ID_KEYS) {
    if (key === 'bp_id') continue;
    const value = urlParams?.get(key) || (cookie as Record<string, any>)[key] || ephemeral[key] || '';
    if (typeof value === 'string' && value !== '') out[key] = value;
  }
  const partner = pickBpIdForDecoration(urlParams, cookie, ephemeral);
  if (partner) {
    out.bp_id = partner.bp_id;
    if (typeof partner.bp_ts === 'number' && Number.isFinite(partner.bp_ts))
      out[BP_TS_PARAM] = String(Math.floor(partner.bp_ts));
  }

  const utmFromEphemeral = ephemeral;
  const utmFromCookie = cookie as Record<string, any>;
  const utmFromUrl = urlParams ? extractUtmParams(urlParams) : {};
  for (const source of [utmFromEphemeral, utmFromCookie, utmFromUrl]) {
    for (const key of Object.keys(source)) {
      if (!UTM_KEY_PATTERN.test(key)) continue;
      const value = (source as Record<string, any>)[key];
      if (typeof value === 'string' && value !== '') out[key] = value;
    }
  }
  return out;
};

/**
 * Append the captured Google click ids to a dashboard URL, without clobbering
 * ids the URL already carries (e.g. a `?plan_name=` link that also has `gclid`).
 * Returns the input unchanged when there is nothing to add or the URL can't be
 * parsed. Used both by the runtime anchor decorator and by direct `window.open`
 * CTAs.
 */
const decorateDashboardUrl = (rawUrl: string): string => {
  const ids = getClickIdsForDecoration();
  if (Object.keys(ids).length === 0) return rawUrl;
  try {
    // Base only matters for parsing relative URLs; dashboard links are absolute.
    const url = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : undefined);
    const addsBpId = Boolean(ids.bp_id) && !url.searchParams.has('bp_id');
    for (const [key, value] of Object.entries(ids)) {
      if (key === BP_TS_PARAM) continue;
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    }
    // bp_ts dates the bp_id we add, never one the link already carries.
    if (addsBpId) {
      if (ids[BP_TS_PARAM]) url.searchParams.set(BP_TS_PARAM, ids[BP_TS_PARAM]);
      else url.searchParams.delete(BP_TS_PARAM);
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
};

const Utils = {
  utmManager,
  utmManagerOnConsent,
  getUtmData,
  getClickIdsForDecoration,
  decorateDashboardUrl,
  readSharedConsent,
  writeSharedConsent,
  clearSharedConsent,
  openConsentPreferences,
};

if (typeof window !== 'undefined') {
  (window as any).openConsentPreferences = openConsentPreferences;
}

export default Utils;
